import { ReactNode, ButtonHTMLAttributes } from 'react'
import { ArrowRight, TrendingUp, TrendingDown } from 'lucide-react'

/**
 * Design System "Calm Precision" — primitivos visuais compartilhados.
 * Fonte única da verdade para cards, painéis, badges e botões.
 * Identidade: superfícies brancas, bordas slate suaves, um único acento (primary),
 * cor reservada apenas para significado semântico.
 */

// ==========================================
// TOKENS
// ==========================================

export const card =
  'rounded-2xl bg-white border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.04)]'

// ==========================================
// CARD
// ==========================================

interface CardProps {
  children: ReactNode
  className?: string
  onClick?: () => void
  hoverable?: boolean
  delay?: number
}

export function Card({ children, className = '', onClick, hoverable, delay }: CardProps) {
  const clickable = !!onClick || hoverable
  return (
    <div
      onClick={onClick}
      style={delay !== undefined ? { animationDelay: `${delay}ms` } : undefined}
      className={`${delay !== undefined ? 'animate-rise ' : ''}${card} ${
        clickable
          ? 'cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)]'
          : ''
      } ${className}`}
    >
      {children}
    </div>
  )
}

// ==========================================
// PANEL (card com cabeçalho + ação)
// ==========================================

interface PanelProps {
  title: string
  icon?: ReactNode
  action?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
  delay?: number
}

export function Panel({
  title,
  icon,
  action,
  children,
  className = '',
  bodyClassName = '',
  delay,
}: PanelProps) {
  return (
    <section
      style={delay !== undefined ? { animationDelay: `${delay}ms` } : undefined}
      className={`flex flex-col ${delay !== undefined ? 'animate-rise ' : ''}${card} ${className}`}
    >
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900">
          {icon && <span className="text-slate-400">{icon}</span>}
          {title}
        </h2>
        {action}
      </header>
      <div className={`flex-1 p-5 ${bodyClassName}`}>{children}</div>
    </section>
  )
}

export function PanelLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 text-xs font-bold text-primary-600 transition-colors hover:text-primary-700"
    >
      {children} <ArrowRight className="h-3.5 w-3.5" />
    </button>
  )
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="py-8 text-center text-sm text-slate-400">{children}</p>
}

// ==========================================
// TREND BADGE
// ==========================================

export function TrendBadge({ value }: { value: number }) {
  const positive = value >= 0
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-bold ${
        positive ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
      }`}
    >
      {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {Math.abs(value)}%
    </span>
  )
}

// ==========================================
// STAT CARD
// ==========================================

type StatTone = 'default' | 'positive' | 'negative'

const statToneClass: Record<StatTone, string> = {
  default: 'text-slate-900',
  positive: 'text-emerald-600',
  negative: 'text-rose-600',
}

interface StatCardProps {
  title: string
  value: string | number
  subtitle?: string
  icon: ReactNode
  trend?: number
  tone?: StatTone
  alert?: boolean
  onClick?: () => void
  delay?: number
}

export function StatCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  tone = 'default',
  alert,
  onClick,
  delay = 0,
}: StatCardProps) {
  const clickable = !!onClick
  return (
    <div
      onClick={onClick}
      style={{ animationDelay: `${delay}ms` }}
      className={`group animate-rise ${card} p-5 transition-all duration-200 ${
        clickable
          ? 'cursor-pointer hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)]'
          : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{title}</p>
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-slate-50 text-slate-400 ring-1 ring-slate-100 transition-colors duration-200 group-hover:bg-primary-50 group-hover:text-primary-600 group-hover:ring-primary-100">
          {icon}
        </div>
      </div>
      <div className="mt-3 flex items-end gap-2">
        <p className={`text-[26px] font-extrabold leading-none tracking-tight tabular-nums ${statToneClass[tone]}`}>
          {value}
        </p>
        {trend !== undefined && <TrendBadge value={trend} />}
      </div>
      {subtitle && (
        <p className={`mt-2 text-xs ${alert ? 'font-semibold text-rose-600' : 'text-slate-400'}`}>
          {subtitle}
        </p>
      )}
    </div>
  )
}

// ==========================================
// BADGE (pílulas de status)
// ==========================================

export type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'

const badgeToneClass: Record<BadgeTone, string> = {
  neutral: 'bg-slate-100 text-slate-600',
  primary: 'bg-primary-50 text-primary-700',
  success: 'bg-emerald-50 text-emerald-700',
  warning: 'bg-amber-50 text-amber-700',
  danger: 'bg-rose-50 text-rose-700',
  info: 'bg-sky-50 text-sky-700',
}

export function Badge({
  children,
  tone = 'neutral',
  className = '',
}: {
  children: ReactNode
  tone?: BadgeTone
  className?: string
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${badgeToneClass[tone]} ${className}`}
    >
      {children}
    </span>
  )
}

// ==========================================
// BUTTON
// ==========================================

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost'
export type ButtonSize = 'sm' | 'md' | 'icon'

const buttonVariantClass: Record<ButtonVariant, string> = {
  primary: 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm',
  secondary: 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300',
  danger: 'bg-rose-600 text-white hover:bg-rose-700 shadow-sm',
  ghost: 'text-slate-600 hover:bg-slate-100',
}

const buttonSizeClass: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  icon: 'h-10 w-10',
}

/** Retorna apenas as classes do botão — útil para aplicar em <Link> ou <a>. */
export function buttonClass(variant: ButtonVariant = 'primary', size: ButtonSize = 'md'): string {
  return `inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:pointer-events-none ${buttonSizeClass[size]} ${buttonVariantClass[variant]}`
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
}

export function Button({ variant = 'primary', size = 'md', className = '', ...props }: ButtonProps) {
  return <button className={`${buttonClass(variant, size)} ${className}`} {...props} />
}
